import { css } from "@emotion/react";
import fs from "fs";
import { GetStaticPropsContext, GetStaticPropsResult, NextPage } from "next";
import path from "path";

import PageHead from "~/components/common/PageHead";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { Layout } from "~/lib/client/layout";
import {
  convertMarkdownToHtml,
  separateMarkdownFrontMatter,
} from "~/lib/server/MarkdownHelper";

type Props = {
  body: string;
  metadata: {
    title: string;
    date?: string;
    description: string;
    url: string;
  };
};

export async function getStaticProps(
  context: GetStaticPropsContext
): Promise<GetStaticPropsResult<Props>> {
  if (context.params == undefined) {
    return {
      notFound: true,
    };
  }

  const {
    params: { slug },
  } = context;
  const filePath = path.join(process.cwd(), "data", "info", `${slug}.md`);
  const { body, metadata } = separateMarkdownFrontMatter(filePath);
  const converted = await convertMarkdownToHtml(body);

  return {
    props: {
      body: converted,
      metadata,
    },
  };
}

export async function getStaticPaths() {
  const files = fs.readdirSync(path.join(process.cwd(), "data", "info"));
  const paths = files.map((file) => {
    const slug = file.replace(/\.md$/, "");
    return {
      params: {
        slug,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
}

const Page: NextPage<Props> = (props) => {
  const isSpMode = useIsSpMode();
  const Head = PageHead({
    title: props.metadata.title,
    ogDescription: props.metadata.description,
    ogUrl: props.metadata.url,
  });

  if (isSpMode == undefined) {
    return <>{Head}</>;
  }

  return (
    <>
      {Head}
      <Layout layoutType={"YTLayout"}>
        <main css={rootStyle}>
          <h1>{props.metadata.title}</h1>
          <article
            css={articleStyle}
            dangerouslySetInnerHTML={{ __html: props.body }}
          />
        </main>
      </Layout>
    </>
  );
};

const rootStyle = css`
  margin: 16px auto;
  max-width: 800px;
  padding: 0 1rem;
  min-height: 100vh;
`;

const articleStyle = css`
  ${mediaQueryIsDesktop(css`
    img {
      max-width: 1000px;
    }
  `)};

  ${mediaQueryIsMobile(css`
    img {
      width: 100%;
    }

    video {
      width: 100%;
    }
  `)};

  table {
    border-collapse: collapse;
    width: 100%;
    font-family: Arial, sans-serif;
    font-size: 14px;
    color: #333333;
    margin-bottom: 20px;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
  }

  table th,
  table td {
    padding: 8px;
    text-align: left;
    vertical-align: middle;
    border-top: 1px solid #dddddd;
  }

  table th {
    background-color: #f2f2f2;
    font-weight: bold;
    text-transform: uppercase;
  }

  table td a {
    color: #007acc;
    text-decoration: none;
  }

  table td a:hover {
    text-decoration: underline;
  }

  table tr {
    background-color: #f5f5f5;
  }

  table tr:hover {
    background-color: #e2e2e2;
  }

  @media screen and (max-width: 480px) {
    table th,
    table td {
      padding: 4px;
    }
  }

  a {
    text-decoration: underline;
  }
`;

export default Page;
